import { Params } from '@angular/router';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Exhibitor, KeyedExhibitorPostsList, KeyedExhibitorProductsList } from '@planone/bau365/util-fair-data';
import { selectQueryParams, selectRouteParams } from '@planone/shared/util-ngrx';

import { DATA_ACCESS_EXHIBITORS_FEATURE_KEY, ExhibitorsState } from './data-access-exhibitors.reducer';

import { ExhibitorSearchResult } from '../../../interfaces/exhibitor.interfaces';
import * as DataAccessFairsSelectors from '../../fairs/entity/data-access-fairs.selectors';

export const getDataAccessExhibitorsState = createFeatureSelector<ExhibitorsState>(DATA_ACCESS_EXHIBITORS_FEATURE_KEY);

export const getExhibitorId = createSelector(selectRouteParams, (routerParams: Params) => {
  return typeof routerParams !== 'undefined' && routerParams['id'] != null ? routerParams['id'] : undefined;
});

export const getExhibitor = createSelector(
  getDataAccessExhibitorsState,
  getExhibitorId,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, exhibitorId: string, fairId: string) =>
    typeof exhibitorId !== 'undefined' ? state[fairId]?.exhibitors[exhibitorId] : undefined
);

export const getCoExhibitors = createSelector(
  getDataAccessExhibitorsState,
  getExhibitorId,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, exhibitorId: string, fairId: string) =>
    state[fairId]?.coExhibitors[exhibitorId]
      ? state[fairId]?.coExhibitors[exhibitorId].map((id) => state[fairId]?.exhibitors[id])
      : undefined
);

export const getParentExhibitor = createSelector(
  getDataAccessExhibitorsState,
  getExhibitorId,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, exhibitorId: string, fairId: string) => {
    const exhibitor = exhibitorId != null ? state[fairId]?.exhibitors[exhibitorId] : undefined;

    if (exhibitor && exhibitor.parent_exhibitor_id) {
      return state[fairId]?.exhibitors[exhibitor.parent_exhibitor_id];
    }

    return undefined;
  }
);

export const getExhibitorById = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string, props: { exhibitorId?: string }) => {
    if (props.exhibitorId && state[fairId]?.exhibitors && state[fairId]?.exhibitors[props.exhibitorId]) {
      return state[fairId]?.exhibitors[props.exhibitorId];
    }

    return undefined;
  }
);

export const getExhibitorFromDirectoryById = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string, props: { exhibitorId?: string; startingLetter?: string }) => {
    if (props.exhibitorId && props.startingLetter && state[fairId]?.directory?.letter?.[props.startingLetter]) {
      return state[fairId]?.directory.letter[props.startingLetter].find((searchResult) => {
        const exhibitorSearchResult = searchResult as ExhibitorSearchResult;

        return exhibitorSearchResult?.exhibitor?.id === props.exhibitorId;
      });
    }

    return undefined;
  }
);

export const getExhibitorDirectory = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) => (state && state[fairId]?.exhibitors ? state[fairId]?.exhibitors : {})
);
export const getExhibitorNextCursor = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) => state[fairId]?.nextCursor
);
export const getExhibitorDirectoryStatus = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) => state[fairId]?.directoryStatus ?? false
);
export const getActiveExhibitorLoading = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) => state[fairId]?.activeExhibitorLoading ?? false
);
export const getActiveExhibitorError = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) =>
    state[fairId]?.activeExhibitorError ? state[fairId]?.activeExhibitorError : undefined
);

export const getExhibitorExhibitorProducts = createSelector(
  getDataAccessExhibitorsState,
  getExhibitorId,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, exhibitorId: string, fairId: string) => {
    const data: KeyedExhibitorProductsList | undefined =
      exhibitorId != null ? state[fairId]?.exhibitors[exhibitorId]?.products?.items : undefined;

    if (data) {
      return Object.values(data);
    }

    return [];
  }
);
export const getActiveExhibitorProductsLoading = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) => state[fairId]?.activeExhibitorProductsLoading ?? false
);
export const getActiveExhibitorProductsError = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) =>
    state[fairId]?.activeExhibitorProductsError ? state[fairId]?.activeExhibitorProductsError : undefined
);
export const getProductsNextCursor = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string, props: { exhibitorId: string }) =>
    state[fairId]?.exhibitors[props.exhibitorId].products?.cursors?.next
);

export const getExhibitorExhibitorPosts = createSelector(
  getDataAccessExhibitorsState,
  getExhibitorId,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, exhibitorId: string, fairId: string) => {
    const data: KeyedExhibitorPostsList | undefined =
      exhibitorId != null ? state[fairId]?.exhibitors[exhibitorId]?.posts?.items : undefined;

    if (data) {
      return Object.values(data);
    }

    return [];
  }
);

export const getActiveExhibitorEventsLoading = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) => state[fairId]?.activeExhibitorEventsLoading ?? false
);
export const getActiveExhibitorEventsError = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) =>
    state[fairId]?.activeExhibitorEventsError ? state[fairId]?.activeExhibitorEventsError : undefined
);

export const getActiveExhibitorPostsLoading = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) => state[fairId]?.activeExhibitorPostsLoading ?? false
);
export const getActiveExhibitorPostsError = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) =>
    state[fairId]?.activeExhibitorPostsError ? state[fairId]?.activeExhibitorPostsError : undefined
);
export const getPostsNextCursor = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string, props: { exhibitorId: string }) =>
    state[fairId]?.exhibitors[props.exhibitorId].posts?.cursors?.next
);

export const getExhibitorContactPerson = createSelector(
  getDataAccessExhibitorsState,
  getExhibitorId,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, exhibitorId: string, fairId: string) => {
    if (typeof state[fairId]?.activeExhibitorContactPerson !== 'undefined') {
      const exhibitor: Exhibitor | undefined = exhibitorId != null ? state[fairId]?.exhibitors[exhibitorId] : undefined;

      if (exhibitor?.contact_persons) {
        return exhibitor?.contact_persons.find((contact) => contact.id === state[fairId]?.activeExhibitorContactPerson);
      }
    }

    return undefined;
  }
);

export const getCharacterSelector = createSelector(selectQueryParams, (routerParams: Params) =>
  typeof routerParams !== 'undefined' && routerParams['character'] != null ? routerParams['character'] : undefined
);

export const getHallSelector = createSelector(selectQueryParams, (routerParams: Params) =>
  typeof routerParams !== 'undefined' && routerParams['hallId'] != null ? routerParams['hallId'] : undefined
);

export const getSearchTermSelector = createSelector(selectQueryParams, (queryParams: Params) =>
  typeof queryParams !== 'undefined' && queryParams['searchTerm'] != null ? queryParams['searchTerm'] : undefined
);

export const getHallList = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) => state[fairId]?.directoryStatus.loaded.availableHalls
);

export const getLetterList = createSelector(
  getDataAccessExhibitorsState,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, fairId: string) => state[fairId]?.directoryStatus.loaded.availableLetters
);

export const getExhibitorDirectorySearchResults = createSelector(
  getDataAccessExhibitorsState,
  getCharacterSelector,
  getHallSelector,
  getSearchTermSelector,
  DataAccessFairsSelectors.getActiveFairId,
  (state: ExhibitorsState, character, hall, searchTerm, fairId: string) => {
    if (searchTerm) {
      return Object.values(state[fairId]?.directory.search[searchTerm] ?? {});
    } else if (character) {
      return Object.values(state[fairId]?.directory.letter[character] ?? {});
    } else {
      return Object.values(state[fairId]?.directory.hall[hall] ?? {});
    }
  }
);
